import React, { useMemo } from 'react';
import {
  listItemSectionBuilder,
  Search,
  SearchProps,
} from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import { useTemplatesContext } from '@components/Contexts/TemplatesContext';

export const SEARCH_MAX_LENGTH = 100;

export const dataHooks = {
  search: 'template-search-field-search',
};

interface BannerSearchProps
  extends Omit<SearchProps, 'placeholder' | 'options' | 'predicate'> {}

const suggestionKeySuffixes = [
  'business-site',
  'landing-page',
  'blog',
  'portfolio',
  'ecommerce',
];
const getSuggestionKey = (suggestionKeySuffix: string) => {
  return `marketplace.templates-page.template-search.suggestion-${suggestionKeySuffix}`;
};

export const TemplateSearchField = ({
  value,
  onEnterPressed: _onEnterPressed,
  onSelect: _onSelect,
  ...props
}: BannerSearchProps) => {
  const { t } = useTranslation();
  const { templateSearchFieldRef } = useTemplatesContext();

  const options = useMemo(() => {
    const suggestions = suggestionKeySuffixes.map((suggestion, index) => {
      return { id: index + 1, value: t(getSuggestionKey(suggestion)) };
    });
    const sectionOptions = [
      listItemSectionBuilder({
        id: 0,
        type: 'title',
        title: t(getSuggestionKey('header')),
      }),
      ...suggestions,
    ];

    return sectionOptions;
  }, [t]);

  const onEnterPressed: SearchProps['onEnterPressed'] = (e) => {
    _onEnterPressed?.(e);
    templateSearchFieldRef?.current?.blur();
  };

  const onSelect: SearchProps['onSelect'] = (option, sameOptionWasPicked) => {
    _onSelect?.(option, sameOptionWasPicked);
    templateSearchFieldRef?.current?.blur();
  };

  return (
    <Search
      dataHook={dataHooks.search}
      ref={templateSearchFieldRef}
      // if you have a value, search won't collapse
      maxLength={SEARCH_MAX_LENGTH}
      value={value}
      placeholder={t('marketplace.templates-page.template-search.placeholder')}
      options={options}
      predicate={() => false}
      onEnterPressed={onEnterPressed}
      onSelect={onSelect}
      popoverProps={{ zIndex: 5 }}
      {...props}
    />
  );
};

export default TemplateSearchField;
